/**
 * InterviewFinishPathDialog — multi-step wizard that ends a SLaM / 3 Borough
 * appointment after the LAST interview module of the day.
 *
 * Two entry points share this one component (see wf.appointmentFinishPathModule
 * in useWorkflowConfig and the builders in utils/appointmentFinishPath.js):
 *   - two-clinician patients: Clinical Interview Review & Synthesis (12_DF.js),
 *     moduleLabel "Clinical Interview"
 *   - one-clinician patients: SSI Review & Synthesis (3_SSIReview.js),
 *     moduleLabel "Semi-Structured Interview"
 * Only the words naming the module change; the steps, buttons and decision
 * payload are identical so clinicians who have used one see the same wizard
 * in the other.
 *
 * Step machine (AssignCliniciansDialog pattern) inside ModuleCompletionDialog:
 *   1. choosePath — select Option 1 (PAD now) or Option 2 (defer PAD), then
 *      a single primary CTA advances (selectable cards + radio affordance,
 *      same pattern as the PAD +ve/−ve step and PAD SelectionOptions)
 *   2a. choosePadVariant → confirmPad → proceed to PAD (Path 1)
 *   2b. generateReport → Generate Report → Documents (Path 2)
 *
 * Intermediate step buttons intentionally avoid AlertDialog.Cancel / bare
 * Action close behaviour so "Back" / path choice keep the dialog open.
 * Completing Path 1 / Generate Report are terminal Actions that may close.
 */

import React, { useEffect, useState } from 'react';
import ModuleCompletionDialog, {
  CancelButton,
  PrimaryActionButton,
  GenerateReportButton,
  SummarySection,
} from './ModuleCompletionDialog';
import SelectableOptionCard from './SelectableOptionCard';
import { CI_FINISH_PATHS } from '../constants/workflowConfig';

const STEPS = Object.freeze({
  CHOOSE_PATH: 'choosePath',
  CHOOSE_PAD_VARIANT: 'choosePadVariant',
  CONFIRM_PAD: 'confirmPad',
  GENERATE_REPORT: 'generateReport',
});

// Shared Tailwind for non-Action step buttons (Back / Continue mid-wizard).
// Mirrors ModuleCompletionDialog STYLES so the row looks consistent, but
// these must NOT wrap AlertDialog.Cancel/Action or the dialog would close.
const BTN = {
  cancel:
    'py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap',
  primary:
    'flex items-center px-4 py-2 mb-2 font-semibold rounded-md transition-all duration-300 bg-violet-900 text-white hover:bg-violet-800',
  primaryDisabled:
    'flex items-center px-4 py-2 mb-2 font-semibold rounded-md bg-gray-300 text-gray-500 cursor-not-allowed',
};

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function} props.onOpenChange
 * @param {string} [props.moduleLabel='Clinical Interview'] Human-readable name
 *   of the interview module that is finishing. Used in the title, the
 *   descriptions and the Option 2 card so the copy matches the page the
 *   clinician is on.
 * @param {string[]} [props.differentials=[]]
 * @param {string} [props.formulation='']
 * @param {boolean} [props.isSubmitting=false]
 * @param {(payload: { path: string, provisionalAdhdOutcome?: 'positive'|'negative' }) => void|Promise} props.onConfirmPadPath
 * @param {() => void} props.onGenerateReport
 */
export default function InterviewFinishPathDialog({
  open,
  onOpenChange,
  moduleLabel = 'Clinical Interview',
  differentials = [],
  formulation = '',
  isSubmitting = false,
  onConfirmPadPath,
  onGenerateReport,
}) {
  const [step, setStep] = useState(STEPS.CHOOSE_PATH);
  // Path choice on step 1 — null until the clinician picks Option 1 or 2
  // (same "select then Continue" pattern as the PAD +ve/−ve step).
  const [selectedPath, setSelectedPath] = useState(null);
  const [padVariant, setPadVariant] = useState(null); // 'positive' | 'negative'

  // Reset wizard whenever it opens so a previous Path 1/2 choice cannot stick.
  useEffect(() => {
    if (open) {
      setStep(STEPS.CHOOSE_PATH);
      setSelectedPath(null);
      setPadVariant(null);
    }
  }, [open]);

  const titleByStep = {
    [STEPS.CHOOSE_PATH]: `${moduleLabel} Complete`,
    [STEPS.CHOOSE_PAD_VARIANT]: 'Start Post-Assessment Discussion',
    [STEPS.CONFIRM_PAD]: 'Confirm Post-Assessment Discussion',
    [STEPS.GENERATE_REPORT]: `Generate ${moduleLabel} Report`,
  };

  const descriptionByStep = {
    [STEPS.CHOOSE_PATH]:
      'Choose how to continue. Most appointments share the outcome with the patient now; otherwise end here and complete Diagnostic Decision later.',
    [STEPS.CHOOSE_PAD_VARIANT]:
      'Select which Post-Assessment Discussion to run now. The formal Diagnostic Decision is still recorded later in your own time.',
    [STEPS.CONFIRM_PAD]:
      `You will go straight into the Post-Assessment Discussion. ${moduleLabel} outputs continue generating in the background for review afterwards.`,
    [STEPS.GENERATE_REPORT]:
      `Generate the ${moduleLabel} report to review and approve outputs. Diagnostic Decision comes next; Post-Assessment Discussion follows after that.`,
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  const handleChoosePathContinue = () => {
    if (!selectedPath || isSubmitting) return;
    if (selectedPath === CI_FINISH_PATHS.PAD_IN_APPOINTMENT) {
      setStep(STEPS.CHOOSE_PAD_VARIANT);
      return;
    }
    setStep(STEPS.GENERATE_REPORT);
  };

  // Primary CTA label on the path-choice step — mirrors the selected option.
  const choosePathCtaLabel =
    selectedPath === CI_FINISH_PATHS.DEFER_PAD
      ? 'End Appointment and Generate Report'
      : 'Proceed to PAD';

  const actions = (() => {
    if (step === STEPS.CHOOSE_PATH) {
      // Go back left-aligned; single primary CTA on the right (label depends
      // on which option card is selected). Nested full-width flex overrides
      // the dialog action row's default justify-end so Cancel sits left.
      return (
        <div className="w-full flex flex-wrap items-center justify-between gap-3">
          <CancelButton onClick={handleClose} />
          <button
            type="button"
            disabled={!selectedPath || isSubmitting}
            className={
              !selectedPath || isSubmitting ? BTN.primaryDisabled : BTN.primary
            }
            onClick={handleChoosePathContinue}
          >
            {choosePathCtaLabel}
          </button>
        </div>
      );
    }

    if (step === STEPS.CHOOSE_PAD_VARIANT) {
      return (
        <div className="w-full flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className={BTN.cancel}
            onClick={() => {
              setPadVariant(null);
              setStep(STEPS.CHOOSE_PATH);
            }}
          >
            Back
          </button>
          <button
            type="button"
            disabled={!padVariant || isSubmitting}
            className={
              !padVariant || isSubmitting ? BTN.primaryDisabled : BTN.primary
            }
            onClick={() => setStep(STEPS.CONFIRM_PAD)}
          >
            Continue
          </button>
        </div>
      );
    }

    if (step === STEPS.CONFIRM_PAD) {
      return (
        <div className="w-full flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            className={BTN.cancel}
            disabled={isSubmitting}
            onClick={() => setStep(STEPS.CHOOSE_PAD_VARIANT)}
          >
            Go back and edit choice
          </button>
          <PrimaryActionButton
            label={
              isSubmitting
                ? 'Starting…'
                : 'Proceed to Post-Assessment Discussion'
            }
            disabled={isSubmitting || !padVariant}
            onClick={() =>
              onConfirmPadPath({
                path: CI_FINISH_PATHS.PAD_IN_APPOINTMENT,
                provisionalAdhdOutcome: padVariant,
              })
            }
          />
        </div>
      );
    }

    // GENERATE_REPORT — Back stays mid-wizard; Generate Report is terminal.
    return (
      <div className="w-full flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          className={BTN.cancel}
          disabled={isSubmitting}
          onClick={() => setStep(STEPS.CHOOSE_PATH)}
        >
          Back
        </button>
        <GenerateReportButton
          onClick={onGenerateReport}
          disabled={isSubmitting}
        />
      </div>
    );
  })();

  return (
    <ModuleCompletionDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
        else onOpenChange(next);
      }}
      title={titleByStep[step]}
      description={descriptionByStep[step]}
      actions={actions}
    >
      {step === STEPS.CHOOSE_PATH && (
        <>
          <SummarySection title="Emerging Differentials:">
            {differentials.length > 0 ? (
              <ul className="list-disc list-inside text-sm text-gray-700 text-left">
                {differentials.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 italic text-left">
                None selected (ADHD remains the condition referred for).
              </p>
            )}
          </SummarySection>
          {formulation.trim() && (
            <div className="mb-4">
              <h3 className="font-semibold mb-2 text-left">Emerging Formulation:</h3>
              <div className="text-gray-700 text-sm whitespace-pre-wrap rounded-lg p-3 border bg-amber-50 border-amber-200 text-left">
                {formulation}
              </div>
            </div>
          )}
          <div
            className="mb-4 space-y-3 text-left"
            role="radiogroup"
            aria-label={`How to continue after ${moduleLabel}`}
          >
            <SelectableOptionCard
              selected={selectedPath === CI_FINISH_PATHS.PAD_IN_APPOINTMENT}
              onSelect={() => setSelectedPath(CI_FINISH_PATHS.PAD_IN_APPOINTMENT)}
              disabled={isSubmitting}
              title="Option 1 — Share outcome now"
              description="Start the Post-Assessment Discussion in this appointment. You will choose positive or negative ADHD feedback next. Diagnostic Decision can be finished afterwards."
            />
            <SelectableOptionCard
              selected={selectedPath === CI_FINISH_PATHS.DEFER_PAD}
              onSelect={() => setSelectedPath(CI_FINISH_PATHS.DEFER_PAD)}
              disabled={isSubmitting}
              title="Option 2 — End appointment here"
              description={`Generate the ${moduleLabel} report, review outputs later, then complete Diagnostic Decision before Post-Assessment Discussion.`}
            />
          </div>
        </>
      )}

      {step === STEPS.CHOOSE_PAD_VARIANT && (
        <div
          className="mb-4 space-y-3 text-left"
          role="radiogroup"
          aria-label="Post-Assessment Discussion outcome"
        >
          <SelectableOptionCard
            selected={padVariant === 'positive'}
            onSelect={() => setPadVariant('positive')}
            disabled={isSubmitting}
            title="Positive ADHD discussion"
            description="Feedback that ADHD criteria appear to be met (full multi-section PAD)."
          />
          <SelectableOptionCard
            selected={padVariant === 'negative'}
            onSelect={() => setPadVariant('negative')}
            disabled={isSubmitting}
            title="Negative ADHD discussion"
            description="Feedback that ADHD criteria do not appear to be met (shorter PAD)."
          />
        </div>
      )}

      {step === STEPS.CONFIRM_PAD && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
          <p className="font-semibold">
            Starting {padVariant === 'positive' ? 'positive' : 'negative'} ADHD
            Post-Assessment Discussion
          </p>
          <p className="mt-2">
            This does not record the formal Diagnostic Decision. If that later
            differs from this choice, you will see a reminder — the in-clinic PAD
            is not changed automatically.
          </p>
        </div>
      )}

      {step === STEPS.GENERATE_REPORT && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-left text-sm text-gray-700">
          <p>
            Overview order will place Diagnostic Decision ahead of Post-Assessment
            Discussion. PAD unlocks after Diagnostic Decision is completed.
          </p>
        </div>
      )}
    </ModuleCompletionDialog>
  );
}
